import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { UpgradeGate } from "../components/Bits";
import { Swords } from "lucide-react";

export default function BvP() {
  const [q, setQ] = useState("");
  const [batter, setBatter] = useState(null);
  const [pitcher, setPitcher] = useState(null);

  const { data: playersData } = useQuery({ queryKey: ["players"], queryFn: () => api.get("/players").then((r) => r.data) });
  const { data: pitchersData } = useQuery({ queryKey: ["pitchers"], queryFn: () => api.get("/pitchers").then((r) => r.data) });
  const { data: allPData } = useQuery({ queryKey: ["allPitchers"], queryFn: () => api.get("/pitchers/all").then((r) => r.data) });
  const [pq, setPq] = useState("");
  const { data: bvp, isFetching } = useQuery({
    queryKey: ["bvp", batter?.mlb_id, pitcher?.id],
    queryFn: () => api.get(`/bvp?batter=${batter.mlb_id}&pitcher=${pitcher.id}`).then((r) => r.data),
    enabled: !!batter && !!pitcher,
  });

  const players = playersData?.players || [];
  const pitchers = pitchersData?.pitchers || [];
  const allPitchers = allPData?.pitchers || [];
  const filtered = q.trim() ? players.filter((p) => p.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8) : [];
  const pitcherMatches = pq.trim().length >= 2 ? allPitchers.filter((p) => p.name.toLowerCase().includes(pq.trim().toLowerCase())).slice(0, 8) : [];
  const h = bvp?.history;

  return (
    <div className="space-y-5 max-w-4xl">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Batter vs <span className="gold-text">Pitcher</span></h1>
        <p className="text-neutral-500 text-sm mt-1">Real career head to head numbers, straight from MLB. Pick a batter, pick tonight's arm, see the history.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4 fade-up-1">
        <div className="card-surface rounded-xl p-4">
          <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">1. Pick a batter</div>
          {batter ? (
            <div className="flex items-center gap-3">
              <img src={batter.headshot} alt="" className="w-10 h-10 rounded-full object-cover bg-white/5" />
              <div className="flex-1"><div className="font-semibold">{batter.name}</div><div className="text-xs text-neutral-500">{batter.team}</div></div>
              <button onClick={() => setBatter(null)} data-testid="bvp-clear-batter" className="text-xs text-neutral-500 hover:text-red-400 transition-colors duration-200">Change</button>
            </div>
          ) : (
            <>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search batter..." data-testid="bvp-batter-search"
                className="w-full bg-[#0a0a0a] border border-white/15 focus:border-[#a855f7] rounded px-3 py-2 text-sm outline-none transition-colors duration-200" />
              <div className="mt-2 space-y-1">
                {filtered.map((p) => (
                  <button key={p.mlb_id} onClick={() => { setBatter(p); setQ(""); }} data-testid={`bvp-batter-${p.mlb_id}`}
                    className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded text-sm text-neutral-300 hover:bg-white/5 transition-colors duration-200">
                    <img src={p.headshot} alt="" className="w-7 h-7 rounded-full object-cover bg-white/5" />
                    {p.name} <span className="text-neutral-600 text-xs">{p.team}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="card-surface rounded-xl p-4">
          <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">2. Pick a pitcher — any arm this season</div>
          {pitcher ? (
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="font-semibold">{pitcher.name}</div>
                <div className="text-xs text-neutral-500">{pitcher.team} · ERA {pitcher.era ?? "—"} · WHIP {pitcher.whip ?? "—"} · HR/9 {pitcher.hr9 ?? "—"}</div>
              </div>
              <button onClick={() => setPitcher(null)} data-testid="bvp-clear-pitcher" className="text-xs text-neutral-500 hover:text-red-400 transition-colors duration-200">Change</button>
            </div>
          ) : (
            <>
              <input value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Search any pitcher (incl. IL)..." data-testid="bvp-pitcher-search"
                className="w-full bg-[#0a0a0a] border border-white/15 focus:border-[#a855f7] rounded px-3 py-2 text-sm outline-none transition-colors duration-200" />
              <div className="mt-2 space-y-1">
                {pitcherMatches.map((p) => (
                  <button key={p.id} onClick={() => { setPitcher(p); setPq(""); }} data-testid={`bvp-pitcher-${p.id}`}
                    className="flex items-center justify-between w-full text-left px-2 py-1.5 rounded text-sm text-neutral-300 hover:bg-white/5 transition-colors duration-200">
                    <span>{p.name} <span className="text-neutral-600 text-xs">{p.team}</span></span>
                    <span className="text-neutral-600 text-xs font-data">ERA {p.era}</span>
                  </button>
                ))}
                {pq.trim().length >= 2 && pitcherMatches.length === 0 && <div className="px-2 py-1.5 text-xs text-neutral-600" data-testid="bvp-pitcher-no-match">No pitcher matches that spelling</div>}
              </div>
              {pq.trim().length < 2 && (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-600 font-bold mt-3 mb-1.5">Or tonight's probables</div>
                  <select
                    value=""
                    onChange={(e) => setPitcher(pitchers.find((p) => p.id === Number(e.target.value)) || null)}
                    data-testid="bvp-pitcher-select"
                    className="w-full bg-[#0a0a0a] border border-white/15 focus:border-[#a855f7] rounded px-3 py-2 text-sm outline-none transition-colors duration-200">
                    <option value="">Select probable pitcher...</option>
                    {pitchers.map((p) => <option key={`${p.id}-${p.game}`} value={p.id}>{p.name} ({p.team}) vs {p.opponent}</option>)}
                  </select>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {batter && pitcher && (
        <div className="card-gold border border-[#a855f7]/30 rounded-xl p-6 gold-glow fade-up-2" data-testid="bvp-result">
          <div className="flex items-center gap-3 mb-4">
            <Swords size={18} className="text-[#a855f7]" />
            <h3 className="font-display text-xl font-bold uppercase">{batter.name} vs {pitcher.name} <span className="text-neutral-500 text-sm font-normal">career</span></h3>
          </div>
          {isFetching ? (
            <div className="text-neutral-500 font-data text-sm animate-pulse">Pulling the history...</div>
          ) : bvp?.locked ? (
            <UpgradeGate feature="Career batter vs pitcher history" />
          ) : h ? (
            <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2 text-center">
              {[["PA", h.pa], ["AB", h.ab], ["H", h.hits], ["HR", h.hr], ["RBI", h.rbi], ["BB", h.bb], ["K", h.so], ["AVG", h.avg], ["OPS", h.ops]].map(([l, v]) => (
                <div key={l} className="bg-black/40 border border-white/10 rounded p-2.5">
                  <div className="text-[10px] uppercase text-neutral-500">{l}</div>
                  <div className={`font-data text-lg font-bold ${l === "HR" && v > 0 ? "text-[#22c55e]" : ""}`}>{v}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-neutral-400 text-sm" data-testid="bvp-no-history">These two have never faced each other. Fresh matchup, lean on the season profiles.</p>
          )}
        </div>
      )}
    </div>
  );
}
